<template>
  <div class="flex flex-col h-full overflow-hidden">
    <div class="flex mb-2 shrink-0">
      <v-text-field
        v-model="nameQuery"
        class="mr-2"
        prepend-inner-icon="mdi-magnify"
        variant="outlined"
        single-line
        :placeholder="t('transactions.searchPlaceholder')"
        clearable
        density="compact"
        hide-details
      ></v-text-field>
      <v-autocomplete
        v-model="categoryFilter"
        :custom-filter="customFilter"
        class="mr-2"
        append-inner-icon="mdi-chart-pie"
        variant="outlined"
        :items="categoriesList"
        item-title="name"
        item-value="id"
        menu-icon=""
        :placeholder="t('common.category')"
        single-line
        style="max-width: 200px"
        clearable
        density="compact"
        hide-details
      ></v-autocomplete>
      <Amount
        v-model="amountFilter"
        :currency-code="currencyCode"
        :currency-symbol="currencySymbol"
      ></Amount>
      <v-select
        v-model="transactionTypeFilter"
        class="mr-2"
        variant="outlined"
        :items="[
          { title: t('transactions.credit'), value: 'credit' },
          { title: t('transactions.debit'), value: 'debit' },
        ]"
        :placeholder="t('transactions.type')"
        single-line
        style="max-width: 200px"
        clearable
        density="compact"
        hide-details
      ></v-select>
      <DatePicker v-slot="{ props: _props }" v-model="filteredDates">
        <v-btn v-bind="_props" height="auto" class="mr-2" append-icon="mdi-calendar-range" border>{{
          dateTitle
        }}</v-btn>
      </DatePicker>
      <v-btn
        density="compact"
        class="mr-2"
        style="min-width: 0px"
        height="auto"
        border
        @click="flaggedFilter = !flaggedFilter"
      >
        <v-icon :color="flaggedFilter ? 'error' : undefined">{{
          flaggedFilter ? "mdi-flag" : "mdi-flag-outline"
        }}</v-icon>
      </v-btn>
      <div class="flex gap-2">
        <v-btn
          density="compact"
          height="auto"
          append-icon="mdi-download"
          border
          @click="onDownloadCsv()"
          >{{ t("common.download") }}</v-btn
        >
        <v-btn
          v-if="accountType === 'treezor' && user.isKind('user')"
          density="compact"
          height="auto"
          prepend-icon="mdi-plus"
          color="primary"
          variant="flat"
          @click="showAddTransactionDialog = true"
          >{{ t("transactions.add") }}</v-btn
        >
      </div>
    </div>
    <AddTransactionDialog
      v-if="accountType === 'treezor' && user.isKind('user')"
      v-model="showAddTransactionDialog"
      :account-id="accountId"
    />
    <v-data-table-virtual
      :headers="headers"
      :items="filteredTransactions"
      :loading="loading"
      hover
      fixed-header
      class="grow"
      style="min-height: 0"
      item-key="id"
      :header-props="{ class: 'text-left font-bold' }"
    >
      <template #loading>
        <v-skeleton-loader type="table-tbody@2"></v-skeleton-loader>
      </template>

      <template #item="{ item, props: rowProps, itemRef }">
        <tr
          v-bind="rowProps"
          :ref="itemRef"
          :class="{ 'selected-transaction': selectedTransaction?.id === item.id }"
          :style="canShowTransactionDrawer ? 'cursor: pointer' : ''"
          @click="canShowTransactionDrawer ? handleOpenTransactionDrawer(item) : undefined"
        >
          <td>
            {{ item.name }}
            <UnreadCommentsMark :count="unreadCount(transactionCommentTargets(item))" />
            <v-chip
              v-if="item.is_future"
              class="ml-3"
              density="compact"
              variant="outlined"
              color="primary"
            >
              <span class="text-xs">{{ t("transactions.upcoming") }}</span>
            </v-chip>
          </td>
          <td>
            <template v-if="user.hasRole(supervisorRoles, { account })">
              <v-chip
                v-if="item.suggestedCategoryName"
                :id="'category-menu-' + item.id"
                color="secondary"
                label
              >
                <v-icon
                  v-tooltip="t('transactions.suggestedCategory.tooltip')"
                  start
                  icon="mdi-star-four-points-outline"
                  size="small"
                />
                {{ item.suggestedCategoryName }}
                <span class="inline-flex items-center gap-2 ms-2 me-1">
                  <v-btn
                    v-tooltip="t('transactions.suggestedCategory.accept')"
                    icon="mdi-check"
                    variant="text"
                    density="compact"
                    size="small"
                    :style="{ '--v-hover-opacity': 0.14 }"
                    :aria-label="t('transactions.suggestedCategory.accept')"
                    @click.stop="onAcceptSuggestion(item)"
                  />
                  <v-btn
                    v-tooltip="t('transactions.suggestedCategory.reject')"
                    icon="mdi-close"
                    variant="text"
                    density="compact"
                    size="small"
                    :style="{ '--v-hover-opacity': 0.14 }"
                    :aria-label="t('transactions.suggestedCategory.reject')"
                    @click.stop="onRejectSuggestion(item)"
                  />
                </span>
                <v-icon size="small" @click.stop="onCategoryChipClick(item)"
                  >mdi-chevron-down</v-icon
                >
              </v-chip>
              <v-chip
                v-else
                :id="'category-menu-' + item.id"
                :color="item.uncategorized ? '#9E9E9E' : 'primary'"
                label
                icon="mdi-pencil"
                @click.stop="onCategoryChipClick(item)"
                >{{ item.categoryLabel || t("common.select") }}
                <v-icon class="ml-2">mdi-chevron-down</v-icon>
              </v-chip>
            </template>
            <span v-else>{{ item.categoryLabel || "-" }}</span>
          </td>
          <td :class="[item.amount > 0 && 'text-success']">
            {{ item.amountFormatted }}
          </td>
          <td>
            {{ item.dateFormatted.short }}
          </td>
          <td v-if="canShowTransactionDrawer">
            {{ item.counterparty }}
          </td>
          <td>
            <v-btn
              v-if="user.hasRole(supervisorRoles, { account })"
              :icon="item.is_flagged ? 'mdi-flag' : 'mdi-flag-outline'"
              size="small"
              @click.stop="flagTransaction(item.id, !item.is_flagged)"
            >
              <v-icon :color="item.is_flagged ? 'error' : '#9E9E9E'"></v-icon>
            </v-btn>
            <v-icon
              v-else
              :icon="item.is_flagged ? 'mdi-flag' : 'mdi-flag-outline'"
              :color="item.is_flagged ? 'error' : '#9E9E9E'"
            ></v-icon>
          </td>
          <td v-if="canShowTransactionDrawer">
            <div v-if="item.files?.length" class="inline-flex items-center gap-1">
              <v-icon icon="mdi-paperclip" size="small" variant="text" />
              {{ item.files?.length }}
            </div>
          </td>
          <td v-if="canShowTransactionDrawer">
            <v-btn size="small" variant="text" icon="mdi-eye"></v-btn>
          </td>
        </tr>
      </template>

      <template #no-data>
        <div v-if="pendingSync" class="text-center py-3" data-testid="transactions-pending-sync">
          {{ t("transactions.pendingSync") }}
        </div>
        <div v-else class="text-center py-3">
          {{ t("transactions.noTransactions") }}
        </div>
      </template>
    </v-data-table-virtual>
    <v-menu
      v-if="categoryMenuTargetTransactionId"
      :model-value="true"
      :target="'#category-menu-' + categoryMenuTargetTransactionId"
      :close-on-content-click="false"
      @update:model-value="categoryMenuTargetTransactionId = null"
    >
      <v-card width="300">
        <div class="flex items-center gap-1 p-2">
          <v-autocomplete
            :items="menuItems"
            item-title="name"
            item-value="id"
            :item-props="categoryItemProps"
            :label="t('common.category')"
            prepend-inner-icon="mdi-magnify"
            single-line
            :placeholder="t('common.search')"
            clearable
            density="compact"
            hide-details
            class="grow"
            @update:model-value="onSetCategory($event)"
          ></v-autocomplete>
          <v-btn
            v-if="canSplitCategories && menuTargetTransaction"
            v-tooltip="t('transactions.split.open')"
            icon="mdi-call-split"
            variant="text"
            size="small"
            :aria-label="t('transactions.split.open')"
            @click="openSplitForTransaction(menuTargetTransaction)"
          />
        </div>
      </v-card>
    </v-menu>
  </div>
</template>

<script lang="ts" setup>
import DatePicker from "@/components/DatePicker.vue";
import UnreadCommentsMark from "@/components/UnreadCommentsMark.vue";
import { useAccountCurrency } from "@/composables/useAccountCurrency.ts";
import { useBankAccount } from "@/composables/useBankAccount";
import { useUnreadComments } from "@/composables/useComments";
import { useI18n } from "@/composables/useI18n";
import { useTransactionCategories } from "@/composables/useTransactionCategories";
import { useTransactions } from "@/composables/useTransactions.ts";
import { useToast, useUserStore } from "@/store";
import { useSelectedTransactionStore } from "@/store/selectedTransaction";
import { transactionCommentTargets } from "@/utils/comments";
import { downloadCsv, formatNumberForCsv } from "@/utils/downloadCsv";
import { customFilter } from "@/utils/tools";
import { formatTrackedCategoryNames } from "@/utils/trackedCategoryLabels";
import type { Transaction } from "@/utils/types";
import { getProviderType } from "@aircap/common/account";
import { supervisorRoles } from "@aircap/common/auth";
import {
  getTrackedCategoryAllocations,
  getTrackedCategoryAssignment,
} from "@aircap/common/trackedCategory";
import { captureException } from "@sentry/vue";
import { storeToRefs } from "pinia";
import { computed, nextTick, ref, toRef, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import AddTransactionDialog from "./AddTransactionDialog.vue";
import Amount from "./components/Amount.vue";

const props = defineProps<{
  accountId: string;
  beneficiaryIban?: string;
}>();

const { t, locale } = useI18n();
const selectedTransactionStore = useSelectedTransactionStore();
const { selectedTransaction } = storeToRefs(selectedTransactionStore);
const { openTransactionDrawer, openSplitDialog, patchTransaction } = selectedTransactionStore;
const route = useRoute();
const router = useRouter();
const user = useUserStore();
const { showErrorToast } = useToast();

const accountId = toRef(props, "accountId");
const beneficiaryIban = toRef(props, "beneficiaryIban");
const showAddTransactionDialog = ref(false);

const { account, loadingAccount, bridgeAccount, powensAccount, gclAccount } =
  useBankAccount(accountId);
const { currencyCode, currencySymbol, accountType } = useAccountCurrency(accountId);

const {
  transactions,
  loading: loadingTransactions,
  flagTransaction,
} = useTransactions(accountId, { beneficiaryIban });
const { unreadCount } = useUnreadComments(accountId);

// Comment emails link here to open the discussion
watch(
  [() => route.query.transactionId, transactions],
  ([transactionId]) => {
    if (typeof transactionId !== "string") return;
    const transaction = transactions.value.find(({ id }) => id === transactionId);
    if (!transaction) return;
    openTransactionDrawer(transaction, { comments: true });
    void router.replace({ query: { ...route.query, transactionId: undefined } });
  },
  { immediate: true },
);

const pendingSync = computed(() => {
  switch (account.value?.accountType) {
    case "bridge":
      return !bridgeAccount.value?.synced_at;
    case "powens":
      return !powensAccount.value?.lastUpdateSynced;
    case "gcl":
      return !gclAccount.value?.lastTransactionsSyncedAt;
    case "swan":
    case "treezor":
    case undefined:
      return false;
  }
});

const {
  setCategory,
  acceptSuggestedTrackedCategory,
  rejectSuggestedTrackedCategory,
  categoriesList,
  getSelectorItems,
  categoryItemProps,
  hasTrackedCategories,
  canSplitCategories,
  loadingTrackedCategories,
  resolveTrackedCategoryName,
  resolveActiveTrackedCategoryName,
} = useTransactionCategories(accountId);

const categoryFilter = ref<string | null>(null);
const categoryMenuTargetTransactionId = ref<string | null>(null);

const openMenu = async (itemId: string) => {
  categoryMenuTargetTransactionId.value = null;
  await nextTick();
  categoryMenuTargetTransactionId.value = itemId;
};

const openSplitForTransaction = (transaction: Transaction) => {
  categoryMenuTargetTransactionId.value = null;
  openSplitDialog(transaction);
};

const onCategoryChipClick = (item: Transaction) => {
  if (getTrackedCategoryAssignment(item).mode === "split") {
    if (canSplitCategories.value) {
      openSplitForTransaction(item);
      return;
    }
    openTransactionDrawer(item);
    return;
  }
  void openMenu(item.id);
};

const menuTargetTransaction = computed(() =>
  transactions.value.find((tx) => tx.id === categoryMenuTargetTransactionId.value),
);

const onSetCategory = async (categoryId: string | null) => {
  if (!categoryMenuTargetTransactionId.value || !menuTargetTransaction.value) return;

  try {
    console.log("Setting transaction category...");
    const transactionId = categoryMenuTargetTransactionId.value;
    const amount = menuTargetTransaction.value.amount;
    await setCategory(transactionId, categoryId, amount);
    patchTransaction(transactionId, {
      trackedCategorySplits: categoryId
        ? [{ trackedCategoryId: categoryId, amount: Math.abs(amount) }]
        : undefined,
      suggestedTrackedCategoryId: undefined,
    });
    categoryMenuTargetTransactionId.value = null;
  } catch (cause) {
    const error = new Error("Error updating category", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  }
};

const onAcceptSuggestion = async (item: Transaction) => {
  const suggestedId = item.suggestedTrackedCategoryId;
  if (!suggestedId) return;
  try {
    await acceptSuggestedTrackedCategory(item.id, suggestedId, item.amount);
    patchTransaction(item.id, {
      trackedCategorySplits: [{ trackedCategoryId: suggestedId, amount: Math.abs(item.amount) }],
      suggestedTrackedCategoryId: undefined,
    });
  } catch (cause) {
    const error = new Error("Error accepting suggested category", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  }
};

const onRejectSuggestion = async (item: Transaction) => {
  const suggestedId = item.suggestedTrackedCategoryId;
  if (!suggestedId) return;
  try {
    await rejectSuggestedTrackedCategory(item.id, suggestedId);
    patchTransaction(item.id, {
      suggestedTrackedCategoryId: undefined,
      rejectedSuggestedTrackedCategoryId: suggestedId,
    });
  } catch (cause) {
    const error = new Error("Error rejecting suggested category", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  }
};

const menuItems = computed(() => getSelectorItems(menuTargetTransaction.value?.amount));

const nameQuery = ref<string>("");
const amountFilter = ref<{ min: number; max: number } | null>(null);
const transactionTypeFilter = ref<string | null>(null);
const flaggedFilter = ref<boolean>(false);
const filteredDates = ref<string[]>([]);

const dateTitle = computed(() => {
  const firstDateValue = filteredDates.value[0];
  const lastDateValue = filteredDates.value[filteredDates.value.length - 1];
  if (filteredDates.value.length === 1 && firstDateValue) {
    const firstDate = new Date(firstDateValue);
    return new Intl.DateTimeFormat(locale.value).format(firstDate);
  } else if (firstDateValue && lastDateValue) {
    const firstDate = new Date(firstDateValue);
    const lastDate = new Date(lastDateValue);
    return `${new Intl.DateTimeFormat(locale.value).format(firstDate)} - ${new Intl.DateTimeFormat(locale.value).format(lastDate)}`;
  }

  return "Dates";
});

const loading = computed(
  () => loadingAccount.value || loadingTransactions.value || loadingTrackedCategories.value,
);

const transactionRows = computed(() =>
  transactions.value.map((transaction) => {
    const assignment = getTrackedCategoryAssignment(transaction);
    let suggestedCategoryName: string | null = null;
    const suggestedId = transaction.suggestedTrackedCategoryId;
    if (hasTrackedCategories.value && assignment.mode === "none" && suggestedId)
      suggestedCategoryName = resolveActiveTrackedCategoryName(suggestedId) ?? null;

    let uncategorized = !transaction.category;
    let categoryName = transaction.category?.name ?? null;
    let categoryLabel = categoryName;
    if (hasTrackedCategories.value) {
      uncategorized = assignment.mode === "none";
      categoryName = formatTrackedCategoryNames(assignment, resolveTrackedCategoryName);
      switch (assignment.mode) {
        case "none":
          categoryLabel = null;
          break;
        case "single":
          categoryLabel = resolveTrackedCategoryName(assignment.trackedCategoryId) ?? null;
          break;
        case "split":
          categoryLabel = t("transactions.split.multiple", { count: assignment.splits.length });
          break;
      }
    }
    return { ...transaction, suggestedCategoryName, uncategorized, categoryName, categoryLabel };
  }),
);

const filteredTransactions = computed(() => {
  const dates = new Set(
    filteredDates.value.map((str) => {
      const date = new Date(str);
      return Intl.DateTimeFormat("fr-CA").format(date);
    }),
  );

  return transactionRows.value
    .filter((t) => (dates.size > 0 ? dates.has(t.date) : true))
    .filter((t) => {
      if (!nameQuery.value) return true;
      const query = nameQuery.value.toLowerCase();
      return (
        t.name.toLowerCase().includes(query) ||
        (t.counterparty?.toLowerCase().includes(query) ?? false) ||
        (t.counterpartyIban?.toLowerCase().includes(query) ?? false)
      );
    })
    .filter((t) => {
      if (!amountFilter.value) return true;
      return (
        Math.abs(t.amount) >= amountFilter.value.min && Math.abs(t.amount) <= amountFilter.value.max
      );
    })
    .filter((t) => {
      if (!transactionTypeFilter.value) return true;
      if (transactionTypeFilter.value === "credit") return t.amount > 0;
      if (transactionTypeFilter.value === "debit") return t.amount < 0;
      return true;
    })
    .filter((t) => {
      if (!categoryFilter.value) return true;

      if (hasTrackedCategories.value) {
        return getTrackedCategoryAllocations(t).some(
          (allocation) => allocation.trackedCategoryId === categoryFilter.value,
        );
      }

      return categoryFilter.value === t.category?.id;
    })
    .filter((t) => {
      if (!flaggedFilter.value) return true;
      return t.is_flagged;
    });
});

const headers = computed(() => [
  { key: "name", title: t("common.name"), sortable: true },
  {
    key: "category",
    title: t("common.category"),
    sortable: true,
    sortRaw: (a: { categoryName: string | null }, b: { categoryName: string | null }) =>
      (a.categoryName || "").localeCompare(b.categoryName || ""),
  },
  { key: "amount", title: t("common.amount"), sortable: true },
  { key: "date", title: t("common.date"), sortable: true },
  ...(canShowTransactionDrawer.value
    ? [{ key: "counterparty", title: t("transfers.beneficiaryName"), sortable: true }]
    : []),
  {
    key: "flagged",
    title: t("common.flagged"),
    sortable: true,
    sortRaw: (a: Transaction, b: Transaction) => (b.is_flagged ? 1 : 0) - (a.is_flagged ? 1 : 0),
  },
  ...(canShowTransactionDrawer.value
    ? [{ key: "files", title: t("common.files"), sortable: true }]
    : []),
  ...(canShowTransactionDrawer.value
    ? [{ key: "actions", title: t("common.actions"), sortable: false }]
    : []),
]);

const headersCsv = computed(() => {
  return [
    t("common.name"),
    t("common.category"),
    currencyCode.value ? `${t("common.amount")} (${currencyCode.value})` : t("common.amount"),
    t("common.date"),
  ];
});

const onDownloadCsv = () => {
  const rows = transactionRows.value.map((t) => {
    const date = new Date(t.date);
    const dateStr = Intl.DateTimeFormat("fr-CA").format(date);
    const timeStr = Intl.DateTimeFormat(locale.value, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
    return [t.name, t.categoryName || "-", formatNumberForCsv(t.amount), `${dateStr} ${timeStr}`];
  });
  downloadCsv([headersCsv.value, ...rows], "transactions.csv");
};

const canShowTransactionDrawer = computed(() => {
  return (
    props.beneficiaryIban ||
    (account.value && getProviderType(account.value.accountType) === "proAccount")
  );
});

const handleOpenTransactionDrawer = (transaction: Transaction) => {
  openTransactionDrawer(transaction);
};

watch(
  () => route.query.category,
  (categoryFromRoute) => {
    const categoryValue =
      categoryFromRoute && typeof categoryFromRoute === "string" ? categoryFromRoute : null;
    if (categoryFilter.value !== categoryValue) {
      categoryFilter.value = categoryValue;
    }
  },
  { immediate: true },
);

watch(categoryFilter, (newCategory) => {
  const currentCategory = route.query.category;
  const routeCategoryValue =
    currentCategory && typeof currentCategory === "string" ? currentCategory : null;

  if ((newCategory || null) !== routeCategoryValue) {
    void router.replace({
      ...route,
      query: {
        ...route.query,
        category: newCategory || undefined,
      },
    });
  }
});
</script>

<style scoped>
:deep(.v-field__outline) {
  --v-field-border-opacity: 0.12;
}

.display-on-row-hover {
  opacity: 0;
}

:deep(.v-btn--active),
tr:hover .display-on-row-hover {
  opacity: 1;
}

.selected-transaction {
  background-color: rgba(var(--v-theme-on-surface), 0.08) !important;
}
</style>
